import { Box, CloseButton, Group, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/**
 * PlatformBanner — site-wide announcement banner rendered above MarketingShell
 * and AppShell. Reads /api/__public__/banner which:
 *   - applies audience filtering server-side (so we never leak a banner aimed
 *     at a different audience)
 *   - applies the visible_from / visible_to window
 *   - returns 204 when no banner is enabled / visible / matches
 *
 * Dismissal:
 *   localStorage['banner:<dismissId>:dismissed'] = '1'. The dismissId rotates
 *   on every edit (it's the row's updated_at ms), so dismissed-then-edited
 *   banners reappear automatically.
 *
 * Polling:
 *   staleTime 5 minutes + refetchOnWindowFocus so a freshly-published banner
 *   appears across all open tabs within a focus event.
 */
type BannerResponse = {
  message: string;
  dismissible: boolean;
  dismissId: string;
};

const QUERY_KEY = ['platform-banner'] as const;
const STALE_MS = 5 * 60 * 1000;
const DISMISS_KEY_PREFIX = 'banner:';
const DISMISS_KEY_SUFFIX = ':dismissed';

export function PlatformBanner() {
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchBanner,
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (data === undefined || data === null) {
      setDismissed(false);
      return;
    }
    try {
      const stored = window.localStorage.getItem(localStorageKey(data.dismissId));
      setDismissed(stored === '1');
    } catch {
      // Private mode / blocked storage — treat as not dismissed.
      setDismissed(false);
    }
  }, [data]);

  if (isLoading) return null;
  if (data === undefined || data === null) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(localStorageKey(data.dismissId), '1');
    } catch {
      // Best-effort — still hide in-session.
    }
    setDismissed(true);
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      style={{
        background:
          'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))',
        borderBottom: '1px solid var(--mantine-color-sun-2)',
        paddingBlock: 10,
        paddingInline: 16,
      }}
    >
      <Group justify="center" gap="md" wrap="wrap" align="center">
        <Text size="sm" c="ink.8">
          {data.message}
        </Text>
        {data.dismissible ? (
          <CloseButton size="sm" aria-label="Dismiss banner" onClick={handleDismiss} />
        ) : null}
      </Group>
    </Box>
  );
}

async function fetchBanner(): Promise<BannerResponse | null> {
  const res = await fetch('/api/__public__/banner', {
    credentials: 'include',
  });
  if (res.status === 204) return null;
  if (!res.ok) return null;
  try {
    return (await res.json()) as BannerResponse;
  } catch {
    return null;
  }
}

function localStorageKey(dismissId: string): string {
  return `${DISMISS_KEY_PREFIX}${dismissId}${DISMISS_KEY_SUFFIX}`;
}
